import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/Table';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Tooltip } from '@/components/ui/Tooltip';
import { ActivityTypeIcon } from '@/components/features/activities/ActivityTypeIcon';
import { Search } from 'lucide-react';
import { formatDateTime } from '@/utils/dates';
import { usePagedSelection } from '@/hooks/usePagedSelection';
import {
  ACTIVITY_TYPE_LABELS,
  getActivityAnalysisState,
  VIBRATION_MODE_LABELS,
  getActivitySelectionDisabledReason,
  isActivitySelectable,
  type Activity,
} from '@/types/activity';

interface SelectionPreviewProps {
  activities: Activity[];
  totalCount: number;
  isLoading: boolean;
  pageSize?: number;
  restrictUnavailableActivities?: boolean;
  selectedActivityIds: Set<string>;
  onToggleActivity: (id: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export function SelectionPreview({
  activities,
  totalCount,
  isLoading,
  pageSize = 50,
  restrictUnavailableActivities = true,
  selectedActivityIds,
  onToggleActivity,
  onSelectAll,
  onDeselectAll,
}: SelectionPreviewProps) {
  const {
    displayedItems: displayedActivities,
    displayCount,
    hasMore,
    loadMore,
    allSelected,
    someSelected,
  } = usePagedSelection({
    items: activities,
    selectedIds: selectedActivityIds,
    getId: (activity) => activity.id,
    isSelectable: (activity) =>
      !restrictUnavailableActivities || isActivitySelectable(activity),
    pageSize,
  });

  const handleToggleAll = () => {
    if (allSelected) {
      onDeselectAll();
    } else {
      onSelectAll();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <EmptyState
        icon={<Search className="h-12 w-12" />}
        title="No activities match your filters"
        description="Try adjusting your filters or select different patients"
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted">
          Showing {Math.min(displayCount, totalCount)} of {totalCount} activities
        </p>
      </div>

      <div className="overflow-hidden rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleToggleAll}
                  aria-label="Select all activities"
                />
              </TableHead>
              <TableHead>Activity ID</TableHead>
              <TableHead>Patient</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Vibration</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayedActivities.map((activity) => {
              const isSelected = selectedActivityIds.has(activity.id);
              const isSelectable = !restrictUnavailableActivities || isActivitySelectable(activity);
              const analysisState = getActivityAnalysisState(activity);
              const disabledReason = restrictUnavailableActivities
                ? getActivitySelectionDisabledReason(activity)
                : undefined;
              const statusLabel = restrictUnavailableActivities
                ? analysisState === 'failed'
                  ? 'Failed'
                  : analysisState === 'unanalyzed'
                    ? 'Pending Analysis'
                    : null
                : null;
              const displayActivityId = (activity.activityStreamId || activity.id).replace(/^activity-/, '');
              return (
                <TableRow
                  key={activity.id}
                  className={!isSelected ? 'opacity-50' : ''}
                >
                  <TableCell>
                    {disabledReason ? (
                      <Tooltip content={disabledReason}>
                        <Checkbox
                          checked={isSelected}
                          disabled={!isSelectable}
                          onChange={() => onToggleActivity(activity.id)}
                          aria-label={`Select activity ${displayActivityId}`}
                        />
                      </Tooltip>
                    ) : (
                      <Checkbox
                        checked={isSelected}
                        disabled={!isSelectable}
                        onChange={() => onToggleActivity(activity.id)}
                        aria-label={`Select activity ${displayActivityId}`}
                      />
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-sm text-muted">
                    {displayActivityId}
                  </TableCell>
                  <TableCell className="text-muted">
                    <div className="flex flex-col">
                      <span>{activity.patientName || activity.patientId}</span>
                      {activity.patientUsername && (
                        <span className="text-xs text-muted-foreground">{activity.patientUsername}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <ActivityTypeIcon type={activity.type} size="sm" />
                      <div className="flex flex-col gap-1">
                        <span>{ACTIVITY_TYPE_LABELS[activity.type]}</span>
                        {statusLabel && (
                          <Tooltip content={disabledReason ?? statusLabel}>
                            <Badge
                              variant={analysisState === 'failed' ? 'danger' : 'warning'}
                              className="w-fit"
                            >
                              {statusLabel}
                            </Badge>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted">
                    {VIBRATION_MODE_LABELS[activity.vibrationMode]}
                  </TableCell>
                  <TableCell className="text-muted">
                    {formatDateTime(activity.timestamp)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {hasMore && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={loadMore}>
            Load More ({activities.length - displayCount} remaining)
          </Button>
        </div>
      )}
    </div>
  );
}
